import { PREF_PREFIX } from '@/utils/prefs';

/**
 * The role-dependent BROWSER STORAGE and what survives a reset of it
 * (Authorization-Plan §6.2 step 5, §13.6). Callers do not use this module
 * directly: auth/clearSession.ts is the one function for sign-out, role
 * switch, session expiry and "no access" (it also clears the in-memory
 * session and the query cache).
 *
 * It works by keep-list, not by remove-list: every localStorage and
 * sessionStorage key is role-dependent (`sessionurl`, tab memory, country
 * and master-data caches, ...) unless it is listed below. A key added later
 * is therefore cleared on a switch by default.
 *
 * Who the user is and what they may do is NOT in browser storage (UI state
 * plan phase 1: the session lives in memory), and neither is the header
 * selection (step A: brand, partner, global filter); the keys older builds
 * wrote (`LEGACY_ACCESS_KEYS`, `LEGACY_SELECTION_KEYS`) are removed in every
 * mode.
 *
 * Modes:
 * - `switch`:  role switch. Keeps the sign-in (Cognito/Amplify, sign-in kit
 *              keys) and unsaved drafts.
 * - `signout`: logout. Also drops the session keys this app owns (token copy,
 *              expires_in); Amplify's own keys are left to Amplify's signOut.
 * - `expired`: session expired (401) or a failed SSO callback: as `signout`,
 *              but keeps `sessionurl` so the next sign-in returns to the page.
 *
 * Each role's last page (`pnet.rolepage.<user id>.<assignment id>`, auth/
 * rolePages.ts) survives a switch and an expired session, and goes on sign-out.
 *
 * Module-level caches (useCountryList, comparisonCache, lib/store.js, ...) and
 * React state are not touched: every caller ends in a full page load.
 */
export type ResetMode = 'switch' | 'signout' | 'expired';

/** Session keys this app owns: kept on a switch, removed on sign-out. */
export const APP_SESSION_KEYS = [
  'access_token', // mock-mode token copy (utils/accessTokenCopy.ts)
  'refresh_token', // legacy, never written any more
  'expires_in',
  `${PREF_PREFIX}role_state_for`, // id only (auth/activeAssignment.ts)
];

/**
 * Access data older builds kept in browser storage: who the user is
 * (`userData`), what the role may do (`permissionsMap`, `useraccess`), the
 * master data (`commondata`), the stored assignment, the cross-tab signal,
 * and the two lists of the old access model (`BrandAccess`: the user's brand
 * ids, `userRole`: the user's role ids).
 * Never written or read any more; removed at boot (from localStorage and
 * sessionStorage) and on every reset, so a browser that last ran an older
 * build does not keep them until the next role switch or sign-out.
 */
export const LEGACY_ACCESS_KEYS = [
  'userData',
  'permissionsMap',
  'useraccess',
  'commondata',
  'pnet_active_assignment',
  'pnet_role_changed',
  'BrandAccess',
  'userRole',
];

const removeFromBothStorages = (keys: string[]): void => {
  for (const getStorage of [() => localStorage, () => sessionStorage]) {
    try {
      const storage = getStorage();
      keys.forEach((key) => storage.removeItem(key));
    } catch {
      /* storage unavailable */
    }
  }
};

/** One-off removal of the legacy access keys (app boot, and every clearSession). */
export function removeLegacyAccessKeys(): void {
  removeFromBothStorages(LEGACY_ACCESS_KEYS);
}

/**
 * The header selection older builds kept in browser storage (UI state plan,
 * step A): header brand (`brand_id`, `brand_id_object`), header partner
 * (`property_id`, `property_data`), the global filter (`globalsearch`) and
 * the contract view's `country_id` hand-over. It lives in memory now
 * (selection/headerSelection.ts) and is kept on the server per user and role
 * assignment. Never written or read any more; removed at boot and on every
 * reset. (`sessionurl` is not part of it: it has to survive the sign-in
 * redirect, so it stays in browser storage.)
 */
export const LEGACY_SELECTION_KEYS = [
  'brand_id',
  'brand_id_object',
  'property_id',
  'property_data',
  'globalsearch',
  'country_id',
];

/** One-off removal of the legacy header selection keys (app boot, and every clearSession). */
export function removeLegacySelectionKeys(): void {
  removeFromBothStorages(LEGACY_SELECTION_KEYS);
}

/** localStorage: the page the next sign-in returns to (ApiProvider.getRedirectionUrl reads it). */
export const RETURN_URL_KEY = 'sessionurl';

/**
 * localStorage: the role assignment whose page `sessionurl` names (id only). Absent for a
 * deep link opened while signed out (routes/SignedOutRedirect.tsx), which any role may use.
 */
export const RETURN_URL_OWNER_KEY = 'sessionurl_ra';

/** The page to return to and its owner: kept only when the session expired. */
const RETURN_URL_KEYS = [RETURN_URL_KEY, RETURN_URL_OWNER_KEY];

/**
 * localStorage: each role's last page, `pnet.rolepage.<user id>.<assignment id>` (and
 * `.preview` during a role preview; auth/rolePages.ts). Only a path; kept on a switch and when
 * the session expired, removed on sign-out.
 */
export const ROLE_PAGE_PREFIX = 'pnet.rolepage.';

/**
 * Role-dependent keys that are ALWAYS cleared, whatever the keep-lists say
 * (part 7, H3): the page to return to (`sessionurl` and its owner, kept only
 * when the session expired), and the legacy access and header selection keys
 * (no longer written, removed from older sessions; the selection itself is in
 * memory and goes with the query cache).
 */
export const ROLE_STATE_KEYS = [
  ...RETURN_URL_KEYS,
  ...LEGACY_SELECTION_KEYS,
  ...LEGACY_ACCESS_KEYS,
];

/** Never removed here: browser/device preferences and the sign-in kit's own state. */
export const PRESERVED_KEYS = [
  'auth_debug',
  'i18nextLng',
  'remember_device_for_mfa', // @asi-lda/pnet-auth-web
  'sso_redirect_started_at', // @asi-lda/pnet-auth-web
  'pnet_auth_mock_token', // @asi-lda/pnet-auth-web
  'session_expired_message', // sessionStorage, read by the login page
];

/** Amplify / Cognito session storage (owned by Amplify). */
export const PRESERVED_PREFIXES = ['CognitoIdentityServiceProvider.', 'com.amplify.', 'amplify-'];

/**
 * Unsaved form drafts: kept on a switch and on sign-out, as before.
 * TODO(§13.6): namespace drafts by `<uid>:<role assignment id>` so switching
 * back restores them and another role never sees them (out of scope of part 4).
 */
export const DRAFT_PREFIXES = [
  'brandOnboarding',
  'partnerOnboarding',
  'fandom',
  'digitalKPIs',
  'socialMedia',
  'directSales',
  'partnerTeam',
  'ticketAllocation',
  'playbook_creation_',
];
export const DRAFT_KEYS = ['briefRun', 'briefCompleted', 'calculationsRun'];

const isDraftKey = (key: string): boolean =>
  DRAFT_KEYS.includes(key) || DRAFT_PREFIXES.some((prefix) => key.startsWith(prefix));

/** True when `key` survives a reset in `mode`. */
export const isKeptOnReset = (key: string, mode: ResetMode): boolean => {
  if (ROLE_STATE_KEYS.includes(key)) return mode === 'expired' && RETURN_URL_KEYS.includes(key);
  if (key.startsWith(ROLE_PAGE_PREFIX)) return mode !== 'signout';
  if (PRESERVED_KEYS.includes(key)) return true;
  if (PRESERVED_PREFIXES.some((prefix) => key.startsWith(prefix))) return true;
  if (isDraftKey(key)) return true;
  if (mode === 'switch' && APP_SESSION_KEYS.includes(key)) return true;
  return false;
};

const keysOf = (storage: Storage): string[] => {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key !== null) keys.push(key);
  }
  return keys;
};

const clearStorage = (getStorage: () => Storage, mode: ResetMode): string[] => {
  try {
    const storage = getStorage();
    const removed = keysOf(storage).filter((key) => !isKeptOnReset(key, mode));
    removed.forEach((key) => storage.removeItem(key));
    return removed;
  } catch {
    return []; // storage unavailable
  }
};

/**
 * Clears the role-dependent browser storage (see above). Returns the removed
 * keys (`local:<key>` / `session:<key>`), for tests and debugging. Use
 * `clearSession()` (auth/clearSession.ts), which also clears the session.
 */
export function resetRoleState(mode: ResetMode = 'switch'): string[] {
  return [
    ...clearStorage(() => localStorage, mode).map((key) => `local:${key}`),
    ...clearStorage(() => sessionStorage, mode).map((key) => `session:${key}`),
  ];
}
